import { observer, useLocalObservable } from 'mobx-react-lite'
import React, { useContext, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { toastError } from '../components/FormComponents/Toasts'
import AudienceInformation from '../models/AudienceInformation'
import Authentication from '../models/Authentication'
import Influencer from '../models/Influencer'
import Post from '../models/Post'
import RatingModel from '../models/Rating'
import axiosService from '../utils/AxiosService'
import { CurrentProjectManagerContext } from './CurrentProjectManagerContext'
export const InfluencerContext = React.createContext({})

export default observer(function InfluencerProvider(props) {
  const { currentProjectManager } = useContext(CurrentProjectManagerContext)
  const { id } = useParams()

  const influencerState = useLocalObservable(() =>
    ({
      influencer: [],
      authentications: [],
      providers: [],
      activePath: '',
      ratings: [],
      addAuthenticationModalOpen: false,
      addAuthenticationModalMode: 'pull',
      addAuthenticationModalProvider: '',
      postsSortOrder: 'desc',
      reachbirdCollaborations: [],
      reachbirdApproved: false,
      reachbirdCollaborationsLoaded: false,
    })
  )

  const getInfluencer = () => {
    axiosService.get(`/api/project_manager/authentications/${id}/influencer`)
    .then(influencer => {
      influencerState.influencer = new Influencer(influencer)
      getReachbirdCollaborations()
    })
    .catch(({ data: { error } }) => toastError(error))
  }

  const getReachbirdCollaborations = () => {
    const influencerId = influencerState.influencer?.id
    if (!influencerId) {
      influencerState.reachbirdCollaborations = []
      influencerState.reachbirdApproved = false
      influencerState.reachbirdCollaborationsLoaded = true
      return
    }
    axiosService.get(`/api/project_manager/influencers/${influencerId}/reachbird_collaborations`)
    .then(({ approved, collaborations }) => {
      influencerState.reachbirdCollaborations = collaborations || []
      influencerState.reachbirdApproved = !!approved
    })
    .catch(({ data: { error } }) => toastError(error))
    .finally(() => {
      influencerState.reachbirdCollaborationsLoaded = true
    })
  }

  const setAddAuthenticationModalOpen = (value) => {
    influencerState.addAuthenticationModalOpen = value
  }

  // Lets callers (e.g. the header's "Edit account" button on a manually-entered
  // authentication) open the add-account modal straight into the "Create manually"
  // tab with the right provider pre-selected, instead of always defaulting to
  // "Pull a new account" and making PMs hunt for the edit affordance.
  const openAddAuthenticationModal = (mode = 'pull', provider = '') => {
    influencerState.addAuthenticationModalMode = mode
    influencerState.addAuthenticationModalProvider = provider
    influencerState.addAuthenticationModalOpen = true
  }

  const setPostsSortOrder = (sortOrder) => {
    influencerState.postsSortOrder = sortOrder
  }

  const getAuthentications = () => {
    axiosService.get(`/api/project_manager/authentications/${id}/authentications`)
    .then(authentications => {
      influencerState.authentications = authentications.map(authentication => new Authentication(authentication))
      influencerState.authentications.forEach(authentication => {
        getTags(authentication)
        getAuthenticationHistories(authentication)
        getPosts(authentication)
        getSimilarAuthentications(authentication)
        getAudienceInformation(authentication)
        if(authentication.provider === 'instagram') {
          getHashtags(authentication)
          getMentions(authentication)
        }
      })
    })
    .catch(({ data: { error } }) => toastError(error))
  }

  const getAudienceInformation = (authentication) => {
    axiosService.get(`/api/project_manager/authentications/${authentication.id}/audience_information`)
    .then(audience_information => authentication.audience_information = new AudienceInformation(audience_information || {}))
    .catch(({ data: { error } }) => toastError(error))
  }

  const getAuthenticationHistories = authentication => {
    axiosService.get(`/api/project_manager/authentications/${authentication.id}/authentication_histories`)
    .then(authentication_histories => authentication.authentication_histories = authentication_histories)
    .catch(({ data: { error } }) => toastError(error))
  }

  const getHashtags = authentication => {
    axiosService.get(`/api/project_manager/authentications/${authentication.id}/authentications_hashtags`)
    .then(authentications_hashtags => authentication.authentications_hashtags = authentications_hashtags)
    .catch(({ data: { error } }) => toastError(error))
  }

  const getMentions = authentication => {
    axiosService.get(`/api/project_manager/authentications/${authentication.id}/authentications_mentions`)
    .then(authentications_mentions => authentication.authentications_mentions = authentications_mentions)
    .catch(({ data: { error } }) => toastError(error))
  }

  const getPosts = (authentication, sortBy = 'desc') => {
    const params = sortBy ? { sort_by: sortBy } : {}
    axiosService.get(`/api/project_manager/authentications/${authentication.id}/posts`, { params })
    .then(posts => authentication.posts = posts.map(post => new Post(post)))
    .catch(({ data: { error } }) => toastError(error))
  }

  const getSponsoredPosts = (authentication) => {
    if(!authentication?.id || authentication.sponsored_posts_loading) return
    authentication.sponsored_posts_loading = true
    axiosService.get(`/api/project_manager/authentications/${authentication.id}/posts/sponsored`)
    .then(posts => {
      authentication.sponsored_posts = posts.map(post => new Post(post))
      authentication.sponsored_posts_loaded = true
    })
    .catch(({ data: { error } }) => toastError(error))
    .finally(() => {
      authentication.sponsored_posts_loading = false
    })
  }

  const getTags = authentication => {
    axiosService.get(`/api/project_manager/authentications/${authentication.id}/tags`)
    .then(tags => authentication.tags = tags)
    .catch(({ data: { error } }) => toastError(error))
  }

  const getSimilarAuthentications = authentication => {
    axiosService.get(`/api/project_manager/authentications/${authentication.id}/similar_authentications`)
    .then(similar_authentications => authentication.similar_authentications = similar_authentications)
    .catch(({ data: { error } }) => toastError(error))
  }

  const getRatings = () => {
    axiosService.get(`/api/project_manager/authentications/${id}/ratings`)
    .then(ratings => influencerState.ratings = ratings)
    .catch(({ data: { error } }) => toastError(error))
  }

  useEffect(() => {
    if(influencerState.authentications.map(auth => auth.id).includes(+id)) return
    getInfluencer()
    getAuthentications()
    getRatings()
  }, [id])

  const currentRating = useMemo(() => influencerState.ratings.find(rating => +rating.project_manager_id === +currentProjectManager.id) || new RatingModel(), [influencerState.ratings.length])

  const professionalRating = useMemo(() => {
    if(!influencerState.ratings.length) return 0

    return (influencerState.ratings.reduce((result, rating) => result + +rating.professional_score, 0) / influencerState.ratings.length)
  }, [influencerState.ratings, influencerState.ratings.length, currentRating.professional_score])

  const qualityRating = useMemo(() => {
    if(!influencerState.ratings.length) return 0

    return (influencerState.ratings.reduce((result, rating) => result + +rating.quality_score, 0) / influencerState.ratings.length)
  }, [influencerState.ratings, influencerState.ratings.length, currentRating.quality_score])

  const combinedRating = useMemo(() => (professionalRating + qualityRating) / 2, [professionalRating, qualityRating])

  useEffect(() => {
    influencerState.providers = influencerState.authentications.map(authentication => authentication.provider)
  }, [influencerState.authentications, influencerState.authentications.length])

  return (
    <InfluencerContext.Provider
      value={{
        influencerState,
        influencer: influencerState.influencer,
        authentications: influencerState.authentications,
        providers: influencerState.providers,
        activePath: influencerState.activePath,
        ratings: influencerState.ratings,
        professionalRating,
        qualityRating,
        combinedRating,
        getInfluencer,
        getAuthentications,
        getRatings,
        getPosts,
        getSponsoredPosts,
        addAuthenticationModalOpen: influencerState.addAuthenticationModalOpen,
        setAddAuthenticationModalOpen,
        addAuthenticationModalMode: influencerState.addAuthenticationModalMode,
        addAuthenticationModalProvider: influencerState.addAuthenticationModalProvider,
        openAddAuthenticationModal,
        postsSortOrder: influencerState.postsSortOrder,
        setPostsSortOrder,
        reachbirdCollaborations: influencerState.reachbirdCollaborations,
        reachbirdApproved: influencerState.reachbirdApproved,
        reachbirdCollaborationsLoaded: influencerState.reachbirdCollaborationsLoaded,
        getReachbirdCollaborations
      }}
    >
      {props.children}
    </InfluencerContext.Provider>
  )
})
